import React from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import PropTypes from 'prop-types';

import Checkbox from '@/components/ui-bridge/core/Checkbox';
import Paper from '@/components/ui-bridge/core/Paper';
import PlanSwitch from '@/components/common/PlanSwitch';
import OrderIcon from '@/components/OrderIcon/OrderIcon';
import styles from './OrderItem.module.scss';
import Forward from '@/svg-icons/Forward';
import StatusExpressa from '@/svg-icons/status-expressa';
import StatusModality from '@/svg-icons/status-modality';
import StatusPremium from '@/svg-icons/status-premium';

const statusIconStyle = {
  fontSize: 22,
  width: 22,
  height: 22,
  margin: '0px 5px',
  verticalAlign: 'bottom',
};

function OrderItem({ checkOne, data, fromPage, inspectionItemSelect, isClientPanel, selectedCheckboxes, user }) {
  const paperClasses = ['border-2 border-[#d9d9d9] bg-[#d5d5d5]'];

  if (user.authRole === 'ROLE_FRANCHISEE') {
    if (data?.status === 'WAITING') {
      paperClasses.push('bg-white');
    } else if (fromPage === 'independent') {
      paperClasses.push('bg-white');
    }
  }

  if (inspectionItemSelect) {
    paperClasses.push('border-[#959595] shadow-[inset_0_0_0_1px_#959595]');
  }

  const title =
    fromPage !== 'client' && fromPage !== 'independent' ? (
      <h2
        className="m-0 text-[14px] font-bold text-black"
        style={{ lineHeight: 1.2, fontFamily: "'Open Sans', sans-serif" }}
      >
        <OrderIcon orderType={data.type} fontSize="30px" />
        <span className="align-middle mr-2">{data.identifier}</span>
        <span className="mx-[8px] text-black">&#9679;</span>
        <span>{data.client}</span>
      </h2>
    ) : (
      <h2
        className="m-0 text-[14px] font-bold text-black"
        style={{ lineHeight: 1.2, fontFamily: "'Open Sans', sans-serif" }}
      >
        <OrderIcon orderType={data.type} fontSize="30px" />
        <span className="align-middle mr-2">{data.identifier}</span>
      </h2>
    );

  const owner = data.inspector;
  const userFranchisee = user?.franchiseeId || user?.profile?.franchisee_id || null;
  const franchiseeNameIsVisible = data?.franchisee_name && data?.franchisee_id !== userFranchisee;

  return (
    <Paper elevation={0} className={paperClasses.join(' ')}>
      <div className="relative m-0 box-border flex flex-row items-center px-[5px] py-2">
        <div className="text-center w-[48px]">
          <Checkbox
            checked={selectedCheckboxes.has(data.id)}
            onChange={e => checkOne(data.id, e)}
            onClick={e => e.stopPropagation()}
            color="primary"
            data-cy={`${data?.id}-order-item-checkbox`}
          />
        </div>

        <div
          className={`grow text-[#858585] text-[12px] px-[10px] py-[5px] ${styles.details}`}
          style={{ fontFamily: "'Open Sans', sans-serif" }}
        >
          <div className="mb-[5px] flex">
            <div className="grow self-center" data-cy={`${data?.id}-order-item-title`}>
              {title}
            </div>

            <div className="self-center text-inherit" data-cy={`${data?.id}-order-item-created-at`}>
              <FormattedDate value={data.created_at} />
            </div>
          </div>

          <div className="mb-[5px] flex">
            <div className="grow self-center" data-cy={`${data?.id}-order-item-type`}>
              {data.type !== null && <FormattedMessage id={data.type || ''} />}
              &#160; &#9679; &#160;
              {data.code}
            </div>

            <div className="self-center text-inherit" data-cy={`${data?.id}-order-item-credits`}>
              <PlanSwitch planToHide="CONTROL">
                <FormattedMessage id="OrderItem.credit" />{' '}
                {data.credits !== undefined ? <FormattedNumber value={data.credits / 100} style="decimal" /> : ' -- '}
              </PlanSwitch>
            </div>
          </div>

          <div className="mb-[5px] flex">
            <div className="grow self-center" data-cy={`${data?.id}-order-item-owner`}>
              <div>
                {owner} &#160; &#160; {data.inspector_document}
              </div>

              {data.schedule && <div>Agendamento &#160; &#9679; &#160; {data.schedule}</div>}
            </div>

            <div className="flex items-center self-center text-inherit">
              {data.client_modality === 'PAY_PER_USE' && (
                <i>
                  <StatusModality viewBox="0 0 16 14" style={statusIconStyle} />
                </i>
              )}

              {data.modality === 'premium' && (
                <i>
                  <StatusPremium viewBox="0 0 34.016 34.016" style={statusIconStyle} />
                </i>
              )}

              {data.urgency === 'express' && (
                <i>
                  <StatusExpressa viewBox="0 0 34.016 34.016" style={statusIconStyle} />
                </i>
              )}

              {data.franchisee_id && data.franchisee_id !== userFranchisee && (
                <i>
                  <Forward style={statusIconStyle} />
                </i>
              )}

              <span
                className="inline-block min-w-[100px] rounded-[15px] bg-[#858585] px-[3px] py-[2px] text-center text-white"
                data-cy={`${data?.id}-order-item-status`}
              >
                {data.status !== null && <FormattedMessage id={data.status} />}
              </span>
            </div>
          </div>

          {franchiseeNameIsVisible && (
            <div className="mb-[5px] flex">
              <b>{data.franchisee_name}</b>
            </div>
          )}

          <div className="flex gap-1" data-cy={`${data?.id}-order-item-tag-list`}>
            {data?.tags?.map(tag => (
              <div
                className="flex h-[18px] w-auto items-center justify-center rounded-[9px] bg-[#388acf] px-[10px] text-[0.5625rem] text-white"
                key={tag}
                data-cy={`${data?.id}-order-item-tag-${tag}-item`}
              >
                {tag}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Paper>
  );
}

OrderItem.propTypes = {
  data: PropTypes.object,
  inspectionItemSelect: PropTypes.bool,
  checkOne: PropTypes.func,
  selectedCheckboxes: PropTypes.object,
  fromPage: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  isClientPanel: PropTypes.bool.isRequired,
};

export default OrderItem;
